// Constantes e helpers puros do KDS (sem React).
import { Bike, ClipboardList, MessageCircle, QrCode, Store } from "lucide-react";
import { CaixaRegistradoraIcon, MesaIcon, MotoEntregaIcon, TotemIcon } from "@/components/icons/ModulosIcons";

export const API = "/api";
export const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export const KDS_STATUSES = ["producao", "pronto"];

export const COLUMNS = [
  {
    status: "producao", label: "Em Produção", nextStatus: "pronto", nextLabel: "Marcar Pronto",
    headerBg: "bg-yellow-500", bg: "bg-yellow-50 dark:bg-yellow-950/30",
    tabAtiva: "bg-yellow-500 text-white", btn: "bg-yellow-500 hover:bg-yellow-600 text-white",
  },
  {
    status: "pronto", label: "Prontos", nextStatus: "concluido", nextLabel: "Concluir",
    headerBg: "bg-green-500", bg: "bg-green-50 dark:bg-green-950/30",
    tabAtiva: "bg-green-600 text-white", btn: "bg-green-600 hover:bg-green-700 text-white",
  },
];

// Chip do modulo: cores fixas (sem variante dark) — o card e' sempre claro
// (branco/amarelo/vermelho), e a variante escura sumia em cima dele.
export const MODULE_CONFIG = {
  Mesas: { icon: MesaIcon, color: "bg-blue-100 text-blue-800" },
  MesaQR: { icon: QrCode, color: "bg-sky-100 text-sky-800" },
  Comandas: { icon: ClipboardList, color: "bg-amber-100 text-amber-800" },
  Delivery: { icon: MotoEntregaIcon, color: "bg-purple-100 text-purple-800" },
  Balcao: { icon: CaixaRegistradoraIcon, color: "bg-teal-100 text-teal-800" },
  Totem: { icon: TotemIcon, color: "bg-violet-100 text-violet-800" },
  ChatBot: { icon: MessageCircle, color: "bg-green-100 text-green-800" },
  Cardapio: { icon: Store, color: "bg-orange-100 text-orange-800" },
  iFood: { icon: Bike, color: "bg-red-100 text-red-800" },
};

export const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  if (url.startsWith("uploads/")) return `/api/${url}`;
  return url.startsWith("/") ? `/api${url}` : url;
};

/** Itens que a cozinha precisa fazer: o item riscado pelo garçom não conta
 *  em lugar nenhum — nem na contagem, nem pra decidir se a comanda ficou pronta. */
export function itensVivos(pedido) {
  return (Array.isArray(pedido?.items) ? pedido.items : []).filter((i) => !i.deleted);
}

/** Itens deletados na mesa, pra DESENHAR riscados com "CANCELADO" (dono,
 *  25/09/2026): antes sumiam do cartão sem aviso e a cozinha podia seguir
 *  fazendo. Só aparecem — não contam, não se selecionam, não se entregam. */
export function itensCancelados(pedido) {
  return (Array.isArray(pedido?.items) ? pedido.items : [])
    .filter((i) => i.deleted)
    .map((i) => ({ ...i, _cancelado: true }));
}

/** Na tela do Garçom, o prato cancelado depois de sair fica 10 min riscado.
 *  Item deletado antes do carimbo `deleted_at` existir não reaparece. */
export const JANELA_CANCELADO_MS = 10 * 60 * 1000;

/** Na cozinha, o envio com TUDO cancelado fica até alguém tocar em "Confirmar
 *  o cancelamento" (dono, 25/09/2026) — a marca `cancelamento_visto_at` vem do
 *  servidor e vale pra todas as telas. Teto de 6 h pra não ficar pra sempre. */
const TETO_CANCELAMENTO_MS = 6 * 60 * 60 * 1000;
export function cancelamentosPendentes(itens, agora = Date.now()) {
  return (itens || []).filter((i) => {
    const t = Date.parse(i.deleted_at || "");
    return !i.cancelamento_visto_at && !Number.isNaN(t) && agora - t <= TETO_CANCELAMENTO_MS;
  });
}
export function canceladosRecentes(itens, agora = Date.now()) {
  return (itens || []).filter((i) => {
    const t = Date.parse(i.deleted_at || "");
    return !Number.isNaN(t) && agora - t <= JANELA_CANCELADO_MS;
  });
}

/** Desde quando o pedido esta na cozinha: entrou em producao (ou foi criado).
 *  E' a base do timer E da cor, nas DUAS colunas — mover pra Prontos nao
 *  zera nada (pedido do dono). `ts_pronto` fica so como registro. */
export function tempoDesde(pedido) {
  return pedido.ts_producao || pedido.created_at;
}

/** "em 4 min" — quanto o produto levou pra ficar pronto, contado de quando a
 *  comanda entrou em produção. Diferencia na comanda o que saiu rápido do resto. */
export function duracaoAtePronto(desde, prontoAt) {
  if (!desde || !prontoAt) return "";
  const seg = Math.max(0, (new Date(prontoAt).getTime() - new Date(desde).getTime()) / 1000);
  if (seg < 60) return "em menos de 1 min";
  const min = Math.round(seg / 60);
  if (min < 60) return `em ${min} min`;
  return `em ${Math.floor(min / 60)}h${String(min % 60).padStart(2, "0")}`;
}

/** "20:14" no fuso do aparelho — hora em que o prato chegou na mesa. */
export function horaCurta(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/** Componentes de um item agrupados por etapa: [{ etapa, itens: [{nome, foto}] }].
 *  O pedido traz a MESMA lista em dois formatos — `subitems` (rico: id, nome,
 *  etapa, foto) e `etapas` (so nomes). Renderizar os dois duplicava tudo na
 *  tela (caso da POINT DO ADDAD). Preferimos `subitems`; `etapas` so quando
 *  nao ha subitems — nunca os dois. */
export function agruparComponentes(item) {
  const subs = Array.isArray(item.subitems) ? item.subitems : [];
  if (subs.length) {
    const ordem = [];
    const mapa = {};
    for (const s of subs) {
      const k = (s.step_name || "").trim();
      if (!(k in mapa)) { mapa[k] = []; ordem.push(k); }
      mapa[k].push({ nome: s.nome || s.name || "", foto: s.photo_url || s.foto_url || null });
    }
    return ordem.map((k) => ({ etapa: k.toUpperCase(), itens: mapa[k] }));
  }
  const etapas = Array.isArray(item.etapas) ? item.etapas : [];
  return etapas
    .map((e) => ({
      etapa: (e.etapa || e.name || e.nome || "").toUpperCase(),
      itens: (e.items || e.itens || []).map((x) =>
        typeof x === "string" ? { nome: x, foto: null } : { nome: x.nome || x.name || "", foto: x.photo_url || null }
      ),
    }))
    .filter((g) => g.itens.length);
}

/** Nivel de urgencia pelo tempo na coluna. Os limiares (minutos) vem de
 *  Configuracoes > Sistema > Preferencias (padrao 18 / 28). E' a cor do CARD INTEIRO (pedido do dono: branco /
 *  amarelo / vermelho), sem animacao — o piscar atrapalhava a cozinha. */
export const LIMIARES_PADRAO = { amarelo: 18, vermelho: 28 };

/** Régua do cartão com produto de setor mais demorado (dono, 25/09/2026): o
 *  tempo de produção do setor, quando passa do vermelho de Preferências, vira
 *  o novo vermelho, e o amarelo cresce na mesma proporção. Menor ou sem tempo:
 *  vale Preferências (o setor é exceção, não encurta a régua). */
export function esticarLimiares(limiares = LIMIARES_PADRAO, tempo) {
  const t = Number(tempo);
  if (!Number.isFinite(t) || t <= limiares.vermelho) return limiares;
  return { amarelo: Math.round((limiares.amarelo * t) / limiares.vermelho), vermelho: t };
}

export function nivelUrgencia(since, limiares = LIMIARES_PADRAO) {
  const min = since ? (Date.now() - new Date(since).getTime()) / 60000 : 0;
  if (min >= limiares.vermelho) return "atrasado";
  if (min >= limiares.amarelo) return "medio";
  return "tranquilo";
}

/** Cores LITERAIS e claras, iguais no tema claro e no escuro: a tela da
 *  cozinha e' lida a dois metros e o dono pediu branco/amarelo/vermelho —
 *  uma tintura sutil de tema escuro (rgba(69,10,10)) nao e' "vermelho".
 *  Por isso texto, chip e botao tambem sao explicitos aqui, e nao tokens do
 *  tema (que no escuro viram cinza claro em cima de um card branco). */
export const NIVEL_CLASSES = {
  tranquilo: {
    card: "bg-white border-zinc-300", header: "bg-zinc-100", texto: "text-zinc-900", suave: "text-zinc-600",
    rotulo: "text-zinc-500", indice: "bg-zinc-200 text-zinc-800", botao: "bg-zinc-900 hover:bg-zinc-800 text-white", timerEscuro: false,
  },
  medio: {
    card: "bg-yellow-300 border-yellow-500", header: "bg-yellow-400", texto: "text-zinc-900", suave: "text-zinc-700",
    rotulo: "text-zinc-600", indice: "bg-yellow-500/50 text-zinc-900", botao: "bg-zinc-900 hover:bg-zinc-800 text-white", timerEscuro: false,
  },
  atrasado: {
    card: "bg-red-500 border-red-700", header: "bg-red-600", texto: "text-white", suave: "text-red-100",
    rotulo: "text-red-100", indice: "bg-white/25 text-white", botao: "bg-white hover:bg-red-50 text-red-700", timerEscuro: true,
  },
};

/** Altura aproximada (px) do miolo do card — decide se o pedido vira card
 *  duplo/triplo no desktop. Numeros vem do layout real: linha de item com
 *  foto ~68px, componente ~30px, rotulo de etapa ~16px, observacao ~44px. */
export function alturaEstimada(pedido) {
  const items = pedido._itens || itensVivos(pedido);
  let h = 0;
  for (const it of items) {
    h += 68 + 12;
    if (it.observacao) h += 44;
    for (const g of agruparComponentes(it)) h += (g.etapa ? 16 : 0) + g.itens.length * 30;
  }
  if (pedido.observacao) h += 48;
  return h;
}

/** O que o rodape do card oferece, por coluna e tipo de entrega (regra do dono):
 *  - Em Producao: sempre "Marcar Pronto".
 *  - Prontos + RETIRADA (pickup): nenhum botao — o caixa finaliza ao receber.
 *  - Prontos + ENTREGA (delivery): "Chamar entregador" (UP Entrega) se a
 *    integracao estiver ativa e ainda nao houver corrida; com corrida em
 *    andamento mostra o status; sem integracao, nenhum botao (o despacho e'
 *    pela tela de Delivery).
 *  - Prontos + balcao/mesa: "Concluir" continua (entregue no balcao).
 *  `up` = { ativo, status } — status e' o item do /entregaai/orders-status. */
export function acaoDoCard(pedido, column, up = {}, selecionados = 0) {
  // Envio com tudo cancelado: só aviso, nenhum botão (nem o Enter do teclado)
  if (pedido._soCancelados) {
    // Garçom: só aviso. Cozinha: a pessoa confirma que viu e o cartão sai
    return pedido._confirmarCancelamento
      ? { tipo: "confirmar_cancelamento", label: "Confirmar o cancelamento" }
      : { tipo: "nenhuma", nota: "Cancelado na mesa · não levar" };
  }
  if (column.status !== "pronto") {
    // Tocar no produto só SELECIONA (pedido do dono, 22/09/2026): quem marca é
    // este botão. Evita o pronto por toque errado e deixa marcar vários de uma vez.
    if (selecionados > 0) {
      return {
        tipo: "marcar_selecionados",
        // Curto de propósito: divide o rodapé do cartão com o número do
        // teclado e o X de limpar, e "como pronto" cortava no meio.
        label: `Marcar ${selecionados} ${selecionados === 1 ? "produto pronto" : "produtos prontos"}`,
      };
    }
    return { tipo: "avancar", label: column.nextLabel };
  }
  // Pedido de MESA não se conclui na cozinha (decisão do dono, 22/09/2026):
  // concluir antes do garçom levar tirava a comanda da tela dele, e quem fecha
  // a conta é o caixa. No lugar, "Entregue na mesa" marca os pratos como
  // entregues — a comanda sai daqui e a mesa continua aberta. Vale também pro
  // cartão parcial: a bebida pode ir antes da comida.
  if (ehDeMesa(pedido)) return { tipo: "entregar", label: "Entregue na mesa" };
  // Cartão parcial: só uma parte da comanda saiu. Não dá pra concluir nem
  // despachar o pedido inteiro com metade dele ainda na chapa.
  if (pedido._parcial) {
    return {
      tipo: "nenhuma",
      nota: pedido._setorCompleto
        ? "Tudo desta tela pronto · o resto é de outra praça"
        : `${(pedido._itens || []).length} de ${pedido._totalDaComanda} · resto na cozinha`,
    };
  }
  const tipo = (pedido.tipo_entrega || "").toLowerCase();
  if (tipo === "pickup") return { tipo: "nenhuma", nota: "Aguardando retirada · o caixa finaliza" };
  if (tipo === "delivery") {
    // Tocou em "Chamar entregador" e o tempo do Desfazer ainda corre: a
    // corrida só é pedida quando ele acaba (lib/acoes.js).
    if (pedido._upPendente) return { tipo: "up_status", label: "Chamando entregador…" };
    if (!up.ativo) return { tipo: "nenhuma", nota: "Despacho pela tela de Delivery" };
    const st = up.status || {};
    const s = (st.entregaai_status || "").toLowerCase();
    if (s === "error") return { tipo: "chamar", label: "Chamar entregador", erro: st.entregaai_error || st.entregaai_status_label || "Falha ao solicitar entregador" };
    if (s && s !== "none" && s !== "cancelled") return { tipo: "up_status", label: st.entregaai_status_label || "Entregador solicitado", entregador: st.courier_name || st.entregador_nome || "" };
    return { tipo: "chamar", label: "Chamar entregador" };
  }
  return { tipo: "avancar", label: column.nextLabel };
}

const NOME_MODULO = {
  Cardapio: "Cardápio Digital", Delivery: "Delivery", Balcao: "Balcão", Totem: "Totem", Mesas: "Mesa", MesaQR: "Mesa QR",
  Comandas: "Comanda", ChatBot: "WhatsApp", iFood: "iFood",
};
const NOME_TIPO = { pickup: "Retirada", delivery: "Entrega" };

/** Chip do card: de onde veio e como sai (pedido do dono). Ex.: "Cardápio
 *  Digital · Entrega", "Delivery · Retirada", "Balcão", "Mesa 5". O tipo so
 *  entra quando faz diferenca na operacao (entrega x retirada). */
/** Pedido servido na mesa (PDV do garçom ou QR do cliente) — é o que a tela
 *  do Garçom leva e o que ganha "entregue". */
export function ehDeMesa(pedido) {
  // Comanda (24/10/2026) se comporta como mesa: a cozinha marca "Entregue" e o
  // pedido só conclui no pagamento, na tela de Comandas
  return pedido?.modulo === "Mesas" || pedido?.modulo === "MesaQR" || pedido?.modulo === "Comandas";
}

/** "Comanda 03 · João": o rótulo vem gravado no endereço do pedido. */
export function rotuloComanda(pedido) {
  const r = String(pedido?.endereco_rua || "").trim();
  return /^Comanda\s/i.test(r) ? r : "Comanda";
}

/** "Mesa 5". Vem do JOIN (mesa_numero); se a listagem nao trouxer, o endereco
 *  do pedido de mesa e' "Mesa N"; por ultimo, o id "mesa-7". */
export function rotuloMesa(pedido) {
  if (pedido?.modulo === "Comandas") return rotuloComanda(pedido);
  const n = pedido.mesa_numero
    || (String(pedido.endereco_rua || "").match(/^Mesa\s+(\S+)/i) || [])[1]
    || String(pedido.mesa_id || "").replace(/^mesa-/i, "");
  return n ? `Mesa ${n}` : "Mesa";
}

export function rotuloOrigem(pedido) {
  const modulo = pedido.modulo || "Cardapio";
  if (modulo === "MesaQR") return `${rotuloMesa(pedido)} · QR`;
  if (modulo === "Mesas") return rotuloMesa(pedido);
  if (modulo === "Comandas") return rotuloComanda(pedido);
  const base = NOME_MODULO[modulo] || modulo;
  const tipo = NOME_TIPO[(pedido.tipo_entrega || "").toLowerCase()];
  return tipo ? `${base} · ${tipo}` : base;
}
